import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import {atomButtonAlignment, atomButtonSizes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useShareApi} from '@adv-mt/mt-hook-use-share-api'
import ModalShare, {MODAL_SHARE_CONTEXTS} from '@adv-mt/mt-modal-share'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import ShareUrlButton, {SHARE_URL_BUTTON_TYPES} from './share-button.js'
import {useGetUrlToShare} from './utils.js'

const NO_OP = () => {}

const BASE_CLASS = 'mt-ActionsShareUrl'

const ActionsShareUrl = ({
  alignment = atomButtonAlignment.CENTER,
  clickedTrackingData,
  icon,
  url,
  size = atomButtonSizes.LARGE,
  type = SHARE_URL_BUTTON_TYPES.ACTION_BUTTON,
  sharedTrackingData,
  trackingOrigin,
  trackingSharedEventName,
  trackingClickEventName,
  trackingContext,
  shareText = '',
  onShared = NO_OP,
  onClick = NO_OP,
  className = '',
  shareContext
}) => {
  const {i18n, domain} = useContext(Context)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const {canShare: isNativeShareAvailable, share: nativeShare} = useShareApi()
  const {track} = useAnalyticsTrack()
  const getUrlToShare = useGetUrlToShare()

  const config = domain.get('config')
  const {SHARE} = config.get('COMMON')
  const {SHARE_METHODS_HIT_INFORMATION, SHARE_METHOD_ID_NATIVE, SHARE_METHOD_ID_CLIPBOARD} = SHARE
  const appName = config.get('APP_NAME')?.toLowerCase()
  const isMobile = config.get('isMobile')
  const useNativeShare = isNativeShareAvailable && isMobile

  const urlToShare = url || (typeof window !== 'undefined' ? window.location.href : '')

  const trackEvent = async ({eventName, provider, origin, trackingData, context}) => {
    const baseTrackingData =
      trackingData ||
      (await domain.get('get_event_tracking_data_use_case').execute({event: eventName, origin, context}))

    const updatedTrackingData = {
      ...baseTrackingData,
      data: {
        ...baseTrackingData.data,
        hit_origin: origin,
        ...(provider
          ? {
              hit_information: SHARE_METHODS_HIT_INFORMATION[provider]
            }
          : {})
      }
    }
    track(updatedTrackingData)
  }

  const trackEventClicked = async () => {
    if (trackingClickEventName) {
      await trackEvent({
        eventName: trackingClickEventName,
        origin: trackingOrigin,
        trackingData: clickedTrackingData,
        context: trackingContext
      })
    }
  }

  const trackEventShared = async ({provider}) => {
    if (trackingSharedEventName) {
      await trackEvent({
        eventName: trackingSharedEventName,
        provider,
        origin: trackingOrigin,
        trackingData: sharedTrackingData,
        context: trackingContext
      })
    }
    onShared()
  }

  const handleNativeShareClick = async ev => {
    if (ev?.stopPropagation) ev.stopPropagation()
    onClick()

    trackEventClicked({provider: SHARE_METHOD_ID_NATIVE})

    try {
      const shared = await nativeShare({url: urlToShare, text: shareText})
      if (shared) trackEventShared({provider: SHARE_METHOD_ID_NATIVE})
    } catch (err) {
      setIsModalOpen(true)
    }
  }

  const handleModalShareClick = ({provider, prompt}) => {
    onClick()

    const shareUrl = getUrlToShare({
      appName,
      provider,
      referer: urlToShare,
      text: shareText,
      prompt
    })

    window.open(shareUrl, '_blank')
    trackEventShared({provider})
  }

  const handleModalShareCopyClick = () => {
    onClick()
    const provider = SHARE_METHOD_ID_CLIPBOARD

    trackEventShared({provider})
  }

  const toggleModal = ({e} = {}) => {
    if (e && e?.stopPropagation) e.stopPropagation()
    const newState = !isModalOpen

    if (newState) {
      onClick()
      trackEventClicked()
    }

    setIsModalOpen(newState)
  }

  const avoidPropagation = e => {
    e.stopPropagation()
  }

  return (
    <div className={`${BASE_CLASS} ${className}`}>
      {useNativeShare ? (
        <ShareUrlButton onClick={handleNativeShareClick} alignment={alignment} icon={icon} size={size} type={type} />
      ) : (
        <>
          <ShareUrlButton onClick={e => toggleModal({e})} alignment={alignment} icon={icon} size={size} type={type} />
          {isModalOpen && (
            <div onClick={avoidPropagation}>
              <ModalShare
                isOpen
                onClose={toggleModal}
                onShareClick={handleModalShareClick}
                onCopyUrlClick={handleModalShareCopyClick}
                urlToShare={urlToShare}
                title={i18n?.t('COMMON.SHARE')}
                shareContext={shareContext}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export {SHARE_URL_BUTTON_TYPES as shareUrlButtonTypes, MODAL_SHARE_CONTEXTS}

ActionsShareUrl.displayName = 'ActionsShareUrl'
ActionsShareUrl.propTypes = {
  alignment: PropTypes.string,
  clickedTrackingData: PropTypes.object,
  icon: PropTypes.node,
  sharedTrackingData: PropTypes.object,
  trackingContext: PropTypes.object,
  url: PropTypes.string,
  size: PropTypes.oneOf(Object.values(atomButtonSizes)),
  type: PropTypes.oneOf(Object.values(SHARE_URL_BUTTON_TYPES)),
  trackingOrigin: PropTypes.string,
  trackingSharedEventName: PropTypes.string,
  trackingClickEventName: PropTypes.string,
  shareText: PropTypes.string,
  onShared: PropTypes.func,
  onClick: PropTypes.func,
  className: PropTypes.string,
  shareContext: PropTypes.oneOf(Object.values(MODAL_SHARE_CONTEXTS))
}

export default ActionsShareUrl
